import { Injectable, Injector, ViewChild } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { ACL_WTAX_LINE_COLUMNS, GUID_EMPTY, TransactionType } from '@core';
import { STColumn, STComponent, STPage } from '@delon/abc/st';
import { ACLService, ACLType } from '@delon/acl';
import { _HttpClient } from '@delon/theme';
import { catchError, EMPTY, Subject, Observable, throwError } from 'rxjs';
import { DocNavService } from 'src/app/core/util/doc-nav.service';
import { DocBaseService } from 'src/app/shared-feature/services/_base/doc-base.service';
import { ValidPostingPeriodCheckingService } from 'src/app/shared/services/valid-posting-period-checking.service';

@Injectable()
export class InvoiceService extends DocBaseService {
  constructor(injector: Injector) {
    super(injector);
    this.aclKey = 'inv';
    this.transactionType = TransactionType.INV;
  }

  setComponentRef(component: any): void {
    this.parentComponent = component;
  }
  get itemDetails() {
    return this.details;
  }
  get acl() {
    return this.injector.get(ACLService);
  }

  get vppSrv() {
    return this.injector.get(ValidPostingPeriodCheckingService);
  }

  get docNav() {
    return this.injector.get(DocNavService);
  }

  form = this.createForm();
  private newLineAddedSubject = new Subject<void>();
  newLineAdded$ = this.newLineAddedSubject.asObservable();

  updatingRate = false;
  paymentInfoItems: any[] = [];
  paymentInfoLoading = false;

  get details() {
    return this.form.controls.details;
  }

  // Add this property to store the validation result
  private _isTaxDateValid: boolean = false;
  // Add a flag to prevent multiple simultaneous requests
  private isValidating = false;

  validateTaxDate() {
    // Return if validation is already in progress
    if (this.isValidating) {
      return;
    }

    this.isValidating = true;

    const requestBody = {
      taxDate: this.form.controls.taxDate.value,
      itemDetails: this.form.controls.details.value || []
    };

    this.http
      .post<{
        isValid: boolean;
      }>('api/sstreturns/ValidateTaxDate?_silent=true', requestBody)
      .subscribe({
        next: response => {
          this._isTaxDateValid = response.isValid;
          this.isValidating = false;
        },
        error: error => {
          this._isTaxDateValid = false;
          this.isValidating = false;
        }
      });
  }

  // Add this getter to access the validation result
  get isTaxDateValid(): boolean {
    return this._isTaxDateValid;
  }

  public get isGovBirCat(): boolean {
    const cust = this.form.controls.customer.value;

    if (cust) {
      return cust.isGovBirCategory;
    }
    return false;
  }

  columns: Array<{
    key: string;
    title: string;
    width: string;
    name: string;
    align?: string;
    acl?: ACLType;
    isGov?: boolean;
  }> = [
    { key: 'account', title: 'Account', width: '120px', name: 'account' },
    {
      key: 'accountName',
      title: 'Account Name',
      width: '180px',
      name: 'accountName'
    },
    {
      key: 'description',
      title: 'Description',
      width: '240px',
      name: 'description'
    },
    {
      key: 'referenceNo',
      title: 'Ref No.',
      width: '180px',
      name: 'referenceNo'
    },
    {
      key: 'amount',
      title: 'Amount',
      width: '120px',
      align: 'right',
      name: 'accAmount'
    },
    {
      key: 'project',
      title: 'Project',
      width: '120px',
      acl: { ability: ['m1.256'] },
      name: 'project'
    },
    {
      key: 'taxCode',
      title: 'Tax',
      width: '120px',
      acl: { ability: ['m1.512'] },
      name: 'taxCode'
    },
    {
      key: 'taxAmount',
      title: 'Tax Amount',
      width: '120px',
      align: 'right',
      acl: { ability: ['m1.512'], mode: 'allOf' },
      name: 'taxAmount'
    },
    // {
    //   key: 'taxExemptionRate',
    //   name: 'taxExemptionRate',
    //   title: 'Exempted Rate',
    //   width: '120px',
    //   align: 'left',
    //   acl: { ability: ['m1.512'], mode: 'allOf' }
    // },
    // {
    //   key: 'taxExemptionAmount',
    //   name: 'taxExemptionAmount',
    //   title: 'Exempted Tax Amount',
    //   width: '120px',
    //   align: 'right',
    //   acl: { ability: ['m1.512'], mode: 'allOf' }
    // },
    {
      key: 'tariffCode',
      title: 'Tariff Code',
      width: '120px',
      align: 'left',
      acl: { ability: ['country.MY', 'm1.8388608'], mode: 'allOf' },
      name: 'tariffCode'
    },
    {
      key: 'taxAmountAdjustment',
      title: 'Tax Adjustment',
      width: '120px',
      align: 'right',
      acl: { ability: ['m1.512'], mode: 'allOf' },
      name: 'taxAmountAdjustment'
    },
    {
      key: 'rentalLeaseExemptedRate',
      title: 'Rental/Lease Exempted Rate',
      width: '120px',
      align: 'left',
      acl: { ability: ['country.MY', 'm1.512'], mode: 'allOf' },
      name: 'rentalLeaseExemptedRate'
    },
    {
      key: 'rentalLeaseExemptedTaxAmount',
      title: 'Rental/Lease Exempted Tax Amount',
      width: '120px',
      align: 'right',
      acl: { ability: ['country.MY', 'm1.512'], mode: 'allOf' },
      name: 'rentalLeaseExemptedTaxAmount'
    },
    {
      key: 'wTaxCode',
      title: 'W/Tax',
      width: '120px',
      acl: ACL_WTAX_LINE_COLUMNS,
      name: 'wTaxCode'
    },
    {
      key: 'wTaxAmount',
      title: 'W/Tax Amount',
      width: '120px',
      align: 'right',
      acl: ACL_WTAX_LINE_COLUMNS,
      name: 'wTaxAmount'
    },
    {
      key: 'wTaxAmountAdjustment',
      title: 'W/Tax Adjustment',
      width: '120px',
      align: 'right',
      acl: ACL_WTAX_LINE_COLUMNS,
      name: 'wTaxAmountAdjustment'
    },
    {
      key: 'wVatCode',
      title: 'WVAT',
      width: '120px',
      acl: { ability: ['country.PH', 'm2.16'], mode: 'allOf' },
      name: 'wVatCode',
      isGov: true
    },
    {
      key: 'wVatRate',
      title: 'WVAT Rate',
      width: '120px',
      align: 'right',
      acl: { ability: ['country.PH', 'm2.16'], mode: 'allOf' },
      name: 'wVatRate',
      isGov: true
    },
    {
      key: 'wVatAmount',
      title: 'WVAT Amount',
      width: '120px',
      align: 'right',
      acl: { ability: ['country.PH', 'm2.16'], mode: 'allOf' },
      name: 'wVatAmount',
      isGov: true
    }
  ];

  initialColumns = this.columns;

  public get visibleColumns() {
    return this.columns.filter(
      col =>
        (!col.acl || this.acl.canAbility(col.acl)) &&
        (!col.isGov || col.isGov === this.isGovBirCat)
    );
  }

  @ViewChild('st') st!: STComponent;
  paymentInfoColumns: STColumn[] = [
    { index: 'paymentDate', title: 'Date', width: 100, render: 'dateTpl' },
    {
      index: 'paymentDocCode',
      title: 'OR #',
      width: 200,
      render: 'doccodeTpl'
    },
    // { index: 'eInvoicePaymentMode', title: 'Payment Mode', width: 200, render: 'paymTpl' },
    { index: 'paymentTerms', title: 'Term', width: 150 },
    { index: 'accountNumber', title: 'Bank Account No.', width: 200 },
    { index: 'paymentRefNo', title: 'Ref. No', width: 200 },
    {
      index: 'paymentAmount',
      title: 'Amount',
      width: 150,
      render: 'amtTpl',
      className: 'text-right'
    }
  ];
  page: STPage = {
    front: false,
    show: false,
    toTop: false
  };

  openDoc(line: any) {
    this.docNav.openDoc(line.paymentDocType, line.paymentDocCode);
  }

  loadPaymentInfo(cdr?: any) {
    let docId = this.id;
    this.paymentInfoItems = [];
    this.paymentInfoLoading = true;

    if (docId) {
      this.http
        .get<any[]>(`api/invoices/GetPaymentInfo?key=${docId}`)
        .subscribe({
          next: (res: any) => {
            var paymentAmount = res.reduce(
              (acc: number, item: any) => acc + (item.paymentAmount ?? 0),
              0
            );
            var netTotal = this.form.controls.netTotalAmount.value ?? 0;
            var outstanding = netTotal - paymentAmount;
            this.form.controls.outstandingAmount.setValue(outstanding);

            this.paymentInfoItems = [...res];
            this.paymentInfoLoading = false;
            cdr?.detectChanges();
          },
          error: () => {
            this.paymentInfoLoading = false;
            cdr?.detectChanges();
          }
        });
    } else {
      this.paymentInfoLoading = false;
    }
    return (this.paymentInfoItems = []);
  }

  clone() {
    if (this.form.controls.isDeferredTax.value && this.form.controls.enableDeferredVat.value === false){
      this.msg.error('Cannot clone Deferred VAT transaction while the Deferred VAT option is turned off. Please enable option to continue');
      return;
    }
    
    let obj = { ...this._data };
    delete obj.id;
    obj.docCode = '';
    obj.docDate = new Date();
    obj.taxDate = new Date();
    obj.isGovCat = obj.customer.isGovBirCategory;

    if (obj.isPast) {
      const newdate = new Date(
        this.cutoffDate.getFullYear(),
        this.cutoffDate.getMonth(),
        0
      );
      obj.docDate = newdate;
      obj.taxDate = newdate;
    }
    obj.isCancelled = false;
    obj.cancelledDate = null;
    obj.cancelledRemark = null;
    return obj;
  }

  new() {
    return this.http.get('api/invoices/new');
  }

  get(id: string): Observable<any> {
    return this.http.get(`api/invoices/${id}`);
  }

  create(body: any) {
    return this.http.post('api/invoices/create/', body);
  }

  update(body: any) {
    return this.http.post('api/invoices/update/', body).pipe(
      catchError(error => {
        if (error.error?.code === 'NEEDS_CONFIRMATION') {
          return new Observable(subscriber => {
            this.modalService.confirm({
              nzTitle: 'Knock-off Confirmation',
              nzContent: `${error.error.message}<br><br>Are you sure you want to save?`,
              nzOkText: 'Proceed',
              nzOkType: 'primary',
              nzOnOk: () => {
                this.http
                  .post('api/invoices/update/?confirmedForKnockOff=true', body)
                  .subscribe({
                    next: res => subscriber.next(res),
                    error: err => subscriber.error(err)
                  });
              },
              nzCancelText: 'Cancel',
              nzOnCancel: () =>
                subscriber.error(new Error('Update canceled by user.'))
            });
          });
        }
        return throwError(() => error);
      })
    );
  }

  override onAction(action: string, data: any): Promise<boolean> {
    const type = action?.toLowerCase();
    switch (action) {
      case 'save':
        return this.vppSrv.check(this.form.value.docDate);
      case 'void':
        return new Promise((resolve, reject) => {
          this.confirmClearKnockoffIfAny(data.id, action)
            .then(() => resolve(true))
            .catch(() => reject(false));
        });
      default:
        return super.onAction(`${type}`, data);
    }
  }

  confirmClearKnockoffIfAny(id: any, action: string): Promise<boolean> {
    return new Promise((resolve, reject) => {
      this.http
        .post(
          'api/Invoices/IsBatchKnockedoff',
          { ids: [id] },
          { _silent: true }
        )
        .subscribe(res => {
          if (res === true) {
            const modalRef = this.modalService.confirm({
              nzTitle: `${action === 'delete' ? 'Delete' : 'Cancel'} Record?`,
              nzContent:
                '<b>Invoice(s) is already knocked-off. Matched data will be cleared.</b>',
              nzOkText: 'Proceed',
              nzOkType: 'primary',
              nzOnOk: () => {
                resolve(true);
                modalRef.destroy();
              },
              nzCancelText: 'Cancel',
              nzOnCancel: () => {
                reject();
                modalRef.destroy();
              }
            });
          } else {
            resolve(true);
          }
        });
    });
  }

  void(body: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http.post('api/invoices/void', body).subscribe({
        next: res => {
          resolve(res);
        },
        error: () => {
          reject();
        }
      });
    });
  }

  devoid(id: string) {
    return this.http.post(`api/invoices/devoid`, { id });
  }

  setBounced() {
    return EMPTY;
  }

  clearChq() {
    return EMPTY;
  }

  setPDC() {}

  setAndSavePDC() {
    return EMPTY;
  }

  setAndSaveUnmarkPDC() {
    return EMPTY;
  }

  transferToSo() {}

  transferToDo() {}

  transferToSinv() {}

  transferToCs() {}

  transferToCsr() {}

  transferToPo() {}

  transferToGrn() {}

  transferToPinv() {}

  transferToCp() {}

  transferToCpf() {}

  doDocTransfer() {
    return EMPTY;
  }

  delete(id: string) {
    return new Promise((resolve, reject) => {
      this.confirmClearKnockoffIfAny(id, 'delete')
        .then(() => {
          this.http
            .delete(`api/invoices`, null, { body: { ids: [id] } })
            .subscribe({
              next: res => {
                resolve(res);
              },
              error: () => {
                reject();
              }
            });
        })
        .catch(() => {
          reject();
        });
    });
  }

  initForm(data: any) {
    this.details.clear();
    const isNew = data.id === GUID_EMPTY;
    let lineCount = isNew ? 1 : (data.details?.length ?? 0);
    this.addLines(lineCount);
    // Initialize BIR 2307 Collected fields
    if (data) {
      this.form.controls.isBIR2307Collected.setValue(data.isBir2307Collected || false);
      this.form.controls.biR2307CollectedDate.setValue(data.bir2307CollectedDate ? new Date(data.bir2307CollectedDate) : null);
    }
  }

  createForm() {
    const form = this.fb.group({
      docCode: ['', [Validators.required, Validators.maxLength(50)]],
      docDate: [new Date(), [Validators.required]],
      taxDate: [new Date(), [Validators.required]],
      customerId: ['', [Validators.required]],
      customer: [<any>null, [Validators.required]],
      currencyId: ['', []],
      currency: [<any>null, [Validators.required]],
      currencyRate: [1, [Validators.required]],
      termId: ['', []],
      term: [<any>null, [Validators.required]],
      agentId: ['', []],
      agent: [<any>null, []],
      wVatCodeId: ['', []],
      wVatCode: [<any>null, []],
      billingAddress: ['', [Validators.maxLength(400)]],
      referenceNo: ['', [Validators.maxLength(50)]],
      description: ['', [Validators.maxLength(250)]],
      isTaxInclusive: [false, []],
      isDeferredTax: [false, []],
      isPast: [false, []],
      docTaxCodeId: ['', []],
      docTaxCode: [<any>null, []],
      isBIR2307Collected: [false, []],
      biR2307CollectedDate: [<any>null, []],
      notes: ['', [Validators.maxLength(5000)]],
      amountExemptedFromTax: [0, []],
      taxExemptionTotalAmount: [0, []],
      projectId: [<any>null, []],
      project: [<any>null, []],
      totalAmount: [0, []],
      totalServiceAmount: [0, []],
      subtotalAmount: [0, []],
      taxTotalAmount: [0, []],
      rentalLeaseExemptedTaxTotalAmount: [0, []],
      wTaxTotalAmount: [0, []],
      wVatTotalAmount: [<number>0, []],
      taxExclusiveTotalAmount: [0, []],
      netTotalAmount: [0, []],
      outstandingAmount: [<number>0, []],
      isGovCat: [false, []],
      fromCashSaleId: ['', []],
      details: this.fb.array([this.createLine()]),
      //Einvoice field
      attention: ['', [Validators.maxLength(200)]],
      customerName: ['', [Validators.maxLength(250)]],
      customerPhone: ['', [Validators.maxLength(100)]],
      email: ['', [Validators.email, Validators.maxLength(300)]],
      customerTinType: [<any>null, []],
      tinNumber: ['', [Validators.maxLength(14)]],
      registrationNo: ['', [Validators.maxLength(40)]],
      salesTaxRegNo: ['', [Validators.maxLength(35)]],
      serviceTaxRegNo: ['', [Validators.maxLength(35)]],
      address1: ['', [Validators.maxLength(300)]],
      address2: ['', [Validators.maxLength(300)]],
      address3: ['', [Validators.maxLength(100)]],
      address4: ['', [Validators.maxLength(100)]],
      zipCode: ['', [Validators.maxLength(20)]],
      city: ['', [Validators.maxLength(50)]],
      state: ['', [Validators.maxLength(100)]],
      country: ['', [Validators.maxLength(100)]],
      enableDeferredVat: [true, []]
    });
    form.controls.agent.valueChanges.subscribe(val => {
      if (!this.observeChanges) return;
      this.form.controls.agentId.setValue(val?.id);
    });
    form.controls.term.valueChanges.subscribe(val => {
      if (!this.observeChanges) return;
      this.form.controls.termId.setValue(val?.id);
    });

    form.controls.docDate.valueChanges.subscribe(val => {
      if (!this.observeChanges) return;
      this.form.controls.taxDate.setValue(val);

      val = new Date(val!);
      const isEarlier =
        new Date(val!.toDateString()) <
        new Date(this.cutoffDate.toDateString());
      if (isEarlier === true) {
        this.form.controls.isPast.setValue(true);
      } else {
        this.form.controls.isPast.setValue(false);
      }
    });

    form.controls.totalAmount.valueChanges.subscribe(val => {
      if (!this.observeChanges) return;
      if (form.controls.isPast.value) {
        this.observeChanges = false;
        this.recalcPastHeader(form);
        this.observeChanges = true;
      }
    });

    form.controls.docTaxCode.valueChanges.subscribe(val => {
      if (!this.observeChanges) return;
      if (val && val.id > 0) {
        form.controls.docTaxCodeId.setValue(val.id);
      } else {
        form.controls.docTaxCodeId.setValue(null);
      }
      this.observeChanges = false;
      this.recalcPastHeader(form);
      this.observeChanges = true;
    });

    form.controls.taxTotalAmount.valueChanges.subscribe(val => {
      if (!this.observeChanges) return;

      if (form.controls.isPast.value) {
        this.observeChanges = false;
        form.controls['taxExclusiveTotalAmount'].setValue(
          this.calc.calcPastTaxExclusiveTotalAmount(form.value)
        );
        form.controls['netTotalAmount'].setValue(
          this.calc.calcPastNetTotalAmount(form.value)
        );
        this.observeChanges = true;
      }
    });

    form.controls.customer.valueChanges.subscribe(val => {
      if (!this.observeChanges) return;
      if (val && val.id > 0) {
        form.controls.customerId.setValue(val.id);
        form.controls.customerName.setValue(val.name);
        form.controls.wVatCodeId.setValue(val?.defaultWVatCodeId);
        this.http
          .get(`api/customers/${val.id}/defaultvalues`)
          .subscribe(res => {
            if (res) {
              form.patchValue({
                agent: res.agent,
                agentId: res.agent?.id,
                term: res.term,
                termId: res.term?.id,
                currency: res.currency,
                currencyId: res.currency.id,
                currencyRate: res.currency.defaultSalesRate,
                billingAddress: this.formatAddress(res),
                isDeferredTax: res.isDeferredTax,
                wVatCode: res.defaultWVatCode,
                attention: res.contactPerson,
                customerName: res.name,
                customerPhone: res.phoneNo1,
                email: res.email,
                customerTinType: res.tinType,
                tinNumber: res.tinNumber,
                isGovCat: res.isGovBirCategory,
                registrationNo: res.registrationNo,
                salesTaxRegNo: res.salesTaxRegNo,
                serviceTaxRegNo: res.serviceTaxRegNo,
                address1: res.address1,
                address2: res.address2,
                address3: res.address3,
                address4: res.address4,
                zipCode: res.zipCode,
                city: res.city,
                state: res.state,
                country: res.country
              });
            }
          });

        if (!this.isGovBirCat) {
          for (let line of this.details.controls) {
            line.patchValue({ wVatCode: null });
            this.recalcLine(line);
          }
          this.recalcHeader(form, this.details.value);
        }
      } else {
        form.controls.customerId.setValue(null);
        form.controls.customerName.setValue(null);
      }
    });

    form.controls.currency.valueChanges.subscribe(val => {
      if (!this.observeChanges) return;
      this.form.controls.currencyId.setValue(val?.id);

      this.observeChanges = false;
      if (val.local) {
        this.form.controls.currencyRate.setValue(1);
      } else {
        this.form.controls.currencyRate.setValue(val?.defaultSalesRate);
      }
      for (let line of this.details.controls) {
        this.recalcLine(line);
      }
      this.recalcHeader(form, this.details.value);
      this.observeChanges = true;
    });

    form.controls.currencyRate.valueChanges.subscribe(_ => {
      if (!this.observeChanges) return;

      this.observeChanges = false;
      for (let line of this.details.controls) {
        this.recalcLine(line);
      }
      this.recalcHeader(form, this.details.value);
      this.observeChanges = true;
    });

    form.controls.isTaxInclusive.valueChanges.subscribe((val: any) => {
      if (!this.observeChanges) return;

      this.observeChanges = false;
      for (let line of this.details.controls) {
        line.controls.isTaxInclusive.setValue(val);
        this.recalcLine(line);
      }
      this.recalcHeader(form, this.details.value);
      this.observeChanges = true;
    });

    form.controls.project.valueChanges.subscribe(val => {
      if (!this.observeChanges) return;
      form.controls.projectId.setValue(val?.id);

      this.observeChanges = false;
      for (let line of this.details.controls) {
        if (line.controls.accountId.value) {
          line.controls.project.setValue(val);
          line.controls.projectId.setValue(val?.id);
        }
      }
      this.observeChanges = true;
    });

    form.valueChanges.subscribe(() => {
      if (!this.observeChanges) return;
      this.modified = true;
    });
    this.userDefaultService.setControlRequired(form.get('agent')!, 'Agent');
    this.userDefaultService.setControlRequired(form.get('project')!, 'Project');

    return form;
  }

  lineValueChanged = new Subject<{ line: FormGroup; controlName: string }>();

  createLine() {
    const line = this.fb.group({
      pos: [0, []],
      numbering: [<any>null, []],
      accountId: [<any>null, []],
      account: [<any>null, []],
      description: ['', [Validators.maxLength(250)]],
      referenceNo: ['', [Validators.maxLength(50)]],
      amount: [0, []],
      taxCodeId: [<any>null, []],
      taxCode: [<any>null, []],
      taxRate: [0, []],
      rentalLeaseExemptedRate: [0, []],
      wTaxCodeId: [<any>null, []],
      wTaxRate: [0, []],
      wTaxCode: [<any>null, []],
      projectId: [<any>null, []],
      project: [<any>null, []],
      tariffCodeId: [<any>null, []],
      tariffCode: [<any>null, []],
      isTaxInclusive: [false, []],
      subAmount: [0, []],
      taxAmount: [0, []],
      rentalLeaseExemptedTaxAmount: [0, []],
      taxAmountAdjustment: [0, []],
      wTaxAmount: [0, []],
      wTaxAmountAdjustment: [0, []],
      wVatCode: [<any>null, []],
      wVatCodeId: [<any>null, []],
      wVatRate: [0, []],
      wVatAmount: [0, []],
      taxExclusiveAmount: [0, []],
      netAmount: [0, []],
      serviceAmount: [0, []],
      amountExemptedTax: [0, []], // Amount Exempted From Tax
      taxExemptionRate: [0, []], 
      taxExemptionAmount: [0, []] // Exempted Tax Amount
    });

    // Select account
    line.controls.account.valueChanges.subscribe(val => {
      if (!this.observeChanges) return;

      if (val) {
        const formData = this.form.value;
        let defaultValues = {
          accountId: val.id,
          description: formData.description,
          referenceNo: formData.referenceNo,
          project: formData.project,
          projectId: formData.projectId,
          tariffCodeId: val.tariffCodeId,
          isTaxInclusive: formData.isTaxInclusive
        };

        // assign default tax code
        this.http.get('api/taxcodes/defaultoutputtax').subscribe(res => {
          if (res && this.acl.can('m1.512')) {
            Object.assign(defaultValues, {
              taxCode: res,
              taxCodeId: res.id,
              taxRate: res.rate,
              taxExemptionRate: res.exemptionRate,
              rentalLeaseExemptedRate: res.rlServiceTaxExemptedRate
            });
          }
          this.observeChanges = false;
          line.patchValue(defaultValues);
          this.recalcLine(line);
          this.recalcHeader(this.form, this.form.controls.details.value);
          this.reorderNumbering();
          this.observeChanges = true;
        });
        if (this.form.controls.wVatCode.value !== null) {
          line.controls.wVatCode.setValue(this.form.controls.wVatCode.value);
          line.controls.wVatCodeId.setValue(
            this.form.controls.wVatCodeId.value
          );
          line.controls.wVatRate.setValue(
            this.form.controls.wVatCode.value.rate
          );
        } else {
          line.patchValue({ wVatRate: 0, wVatAmount: 0, wVatCodeId: null });
        }

        // add new line if the next line doesnt exists
        var curPos = line.value.pos;
        if (curPos) {
          // If the current position is the last line, add a new line
          if (curPos >= this.details.value.length) this.addLines(1);
        }
      } else {
        this.observeChanges = false;
        line.patchValue({
          description: '',
          referenceNo: '',
          taxCode: null,
          taxCodeId: null,
          tariffCodeId: null,
          accountId: null,
          taxRate: 0,
          amount: 0,
          serviceAmount: 0,
          wTaxCode: null,
          wTaxCodeId: null,
          wTaxRate: 0,
          project: null,
          projectId: null,
          wVatCodeId: null,
          wVatAmount: 0,
          wVatRate: 0,
          amountExemptedTax: 0,
          taxExemptionRate: 0,
          taxExemptionAmount: 0
        });

        this.recalcLine(line);
        this.recalcHeader(this.form, this.form.controls.details.value);
        this.reorderNumbering();
        this.observeChanges = true;
      }

      this.lineValueChanged.next({ line, controlName: 'account' });
    });

    line.controls.taxCode.valueChanges.subscribe(val => {
      if (!this.observeChanges) return;
      this.observeChanges = false;
      line.controls.taxCodeId.setValue(val?.id);
      line.controls.taxRate.setValue(val?.rate ?? 0);
      line.controls.taxExemptionRate.setValue(val?.exemptionRate ?? 0);
      line.controls.rentalLeaseExemptedRate.setValue(val?.rlServiceTaxExemptedRate ?? 0);
      if (!val) {
        line.controls.taxAmountAdjustment.setValue(0);
      }
      this.recalcLine(line);
      this.recalcHeader(this.form, this.details.value);
      this.observeChanges = true;

      this.lineValueChanged.next({ line, controlName: 'taxCode' });
    });
    
    line.controls.taxExemptionRate.valueChanges.subscribe((val: any) => {
      if (!this.observeChanges) return;
      this.observeChanges = false;
      line.controls.taxExemptionRate.setValue(val);
      this.recalcLine(line);
      this.recalcHeader(this.form, this.details.value);
      this.observeChanges = true;
      this.lineValueChanged.next({ line, controlName: 'taxExemptionRate' });
    });

    line.controls.wTaxCode.valueChanges.subscribe(val => {
      if (!this.observeChanges) return;
      this.observeChanges = false;
      line.controls.wTaxCodeId.setValue(val?.id);
      line.controls.wTaxRate.setValue(val?.rate ?? 0);
      if (!val) {
        line.controls.wTaxAmountAdjustment.setValue(0);
      }
      this.recalcLine(line);
      this.recalcHeader(this.form, this.details.value);
      this.observeChanges = true;

      this.lineValueChanged.next({ line, controlName: 'wTaxCode' });
    });

    line.controls.wVatCode.valueChanges.subscribe(val => {
      if (!this.observeChanges) return;
      this.observeChanges = false;
      line.controls.wVatCodeId.setValue(val?.id);
      line.controls.wVatRate.setValue(val?.rate ?? 0);
      this.recalcLine(line);
      this.recalcHeader(this.form, this.details.value);
      this.observeChanges = true;

      this.lineValueChanged.next({ line, controlName: 'wVatCode' });
    });

    line.controls.tariffCode.valueChanges.subscribe(val => {
      if (!this.observeChanges) return;
      this.observeChanges = false;
      line.controls.tariffCodeId.setValue(val?.id);
      this.observeChanges = true;
      this.lineValueChanged.next({ line, controlName: 'tariffCode' });
    });

    line.controls.project.valueChanges.subscribe(val => {
      if (!this.observeChanges) return;
      line.controls.projectId.setValue(val?.id);

      this.lineValueChanged.next({ line, controlName: 'project' });
    });

    line.controls.amount.valueChanges.subscribe(val => {
      if (!this.observeChanges) return;
      this.observeChanges = false;
      line.value.amount = val;
      this.recalcLine(line);
      this.recalcHeader(this.form, this.details.value);
      this.observeChanges = true;
    });

    line.controls.taxAmountAdjustment.valueChanges.subscribe(val => {
      if (!this.observeChanges) return;
      this.observeChanges = false;
      this.recalcLine(line);
      this.recalcHeader(this.form, this.details.value);
      this.observeChanges = true;
    });

    line.controls.wTaxAmountAdjustment.valueChanges.subscribe(val => {
      if (!this.observeChanges) return;
      this.observeChanges = false;
      this.recalcLine(line);
      this.recalcHeader(this.form, this.details.value);
      this.observeChanges = true;
    });

    return line;
  }

  updateLatestRate() {
    this.updatingRate = true;
    this.http
      .post('api/currencies/latestrate?_silent=true', {
        currencyCode: this.form.controls.currency.value.code,
        isAR: true
      })
      .subscribe({
        next: res => {
          if (res) {
            this.form.controls.currencyRate.setValue(res);
          }
          this.updatingRate = false;
        },
        error: () => (this.updatingRate = false)
      });
  }

  updateCustInfo(data: any) {
    this.form.controls.customerName.setValue(data.customerName);
    this.form.controls.tinNumber.setValue(data.tinNumber);
    this.form.controls.registrationNo.setValue(data.registrationNo);
    this.form.controls.customerPhone.setValue(data.customerPhone);
    this.form.controls.email.setValue(data.email);
    this.form.controls.salesTaxRegNo.setValue(data.salesTaxRegNo);
    this.form.controls.serviceTaxRegNo.setValue(data.serviceTaxRegNo);
    this.form.controls.customerTinType.setValue(data.customerTinType);
    this.form.controls.zipCode.setValue(data.zipCode);
    this.form.controls.city.setValue(data.city);
    this.form.controls.state.setValue(data.state);
    this.form.controls.country.setValue(data.country);
    this.form.controls.address1.setValue(data.address1);
    this.form.controls.address2.setValue(data.address2);
    this.form.controls.address3.setValue(data.address3);
    this.form.controls.address4.setValue(data.address4);
  }

  reorderNumbering() {
    let numbering = 1;
    for (let i = 0; i < this.details.length; i++) {
      if (this.details.at(i).value.accountId) {
        this.details.at(i).patchValue({ numbering: numbering });
        numbering++;
      }
    }
  }
 
  addLines(count: number = 1) {
    const isTaxIncl = this.form.controls.isTaxInclusive.value;
    for (let i = 0; i < count; i++) {
      let line = this.createLine();
      line.patchValue({
        pos: this.details.length + 1,
        isTaxInclusive: isTaxIncl
      });
      this.details.push(line);
    }
  }

  recalcLine(line: FormGroup) {
    line.controls['subAmount'].setValue(this.calc.calcSubAmount(line.value));
    line.controls['taxAmount'].setValue(this.calc.calcTaxAmount(line.value));
    line.controls['rentalLeaseExemptedTaxAmount'].setValue(this.calc.calcRentalLeaseExemptedTaxAmount(line.value));
    line.controls['wTaxAmount'].setValue(this.calc.calcWTaxAmount(line.value));
    if (line.controls['wVatRate'].value !== null) {
      line.controls['wVatAmount'].setValue(
        this.calc.calcWVatAmount(line.value)
      );
    }
    line.controls['taxExclusiveAmount'].setValue(
      this.calc.calcTaxExclusiveAmount(line.value)
    );
    line.controls['serviceAmount'].setValue(
      this.calc.calcServiceAmount(line.value)
    );
    line.controls['netAmount'].setValue(this.calc.calcNetAmount(line.value));
    line.controls['amountExemptedTax'].setValue(this.calc.calcAmountExemptedTax(line.value));
    line.controls['taxExemptionAmount'].setValue(this.calc.calcTaxExemptionAmount(line.value));
  }

  recalcHeader(header: FormGroup, lines: any[]) {
    if (header.controls['isPast'].value) {
      this.recalcPastHeader(header);
      return;
    }

    header.controls['totalAmount'].setValue(this.calc.calcTotalAmount(lines));
    header.controls['subtotalAmount'].setValue(
      this.calc.calcSubtotalAmount(lines)
    );
    header.controls['taxTotalAmount'].setValue(
      this.calc.calcTaxTotalAmount(lines)
    );
    header.controls['rentalLeaseExemptedTaxTotalAmount'].setValue(
      this.calc.calcRentalLeaseExemptedTaxTotalAmount(lines)
    );
    header.controls['wTaxTotalAmount'].setValue(
      this.calc.calcWTaxTotalAmount(lines)
    );
    this.form.controls['wVatTotalAmount'].setValue(
      this.calc.calcWVatTotalAmount(lines)
    );
    header.controls['taxExclusiveTotalAmount'].setValue(
      this.calc.calcTaxExclusiveTotalAmount(lines)
    );
    header.controls['totalServiceAmount'].setValue(
      this.calc.calcTotalServiceAmount(lines)
    );
    header.controls['netTotalAmount'].setValue(
      this.calc.calcNetTotalAmount(lines)
    );
    header.controls['amountExemptedFromTax'].setValue(
      this.calc.calcTotalAmountExemptedTax(lines)
    );
    header.controls['taxExemptionTotalAmount'].setValue(
      this.calc.calcTotalTaxExemptionAmount(lines)
    );
  }

  recalcPastHeader(header: FormGroup) {
    header.controls['subtotalAmount'].setValue(
      header.controls['totalAmount'].value
    );
    header.controls['taxTotalAmount'].setValue(
      this.calc.calcPastTaxTotalAmount(header.value)
    );
    header.controls['taxExclusiveTotalAmount'].setValue(
      this.calc.calcPastTaxExclusiveTotalAmount(header.value)
    );
    header.controls['netTotalAmount'].setValue(
      this.calc.calcPastNetTotalAmount(header.value)
    );
  }
}
